import type { SlideProps } from "@/engine/app/offsite-config";
import { usePhase, useStore, useWorkshop } from "@/state/store";
import { useSitzungsZahlen } from "@/hooks/useSitzungsZahlen";
import { Timer } from "@/components/primitives/Timer";
import { CodeKulisse } from "./_code-kulisse";

/**
 * Arbeitsphase — acht Minuten, in denen das Gremium auf den Handys promptet.
 *
 * Eigene Folie, weil hier etwas anderes zählt als beim Warten: Dort waren
 * Adresse, PIN und QR-Code das Wichtigste, hier ist es die Zeit. Sie steht
 * deshalb groß in der Mitte, nicht als Nebenzeile.
 *
 * Der Code im Hintergrund ist Kulisse. Er soll zeigen, dass etwas entsteht,
 * ohne dem Timer die Aufmerksamkeit zu nehmen.
 */
export function SlideArbeitsphase(_: SlideProps) {
  const phase = usePhase();
  const { state } = useStore();
  const { timerEndsAt, timerPausedRemainingSec } = useWorkshop().phase;
  const zahlen = useSitzungsZahlen(phase === "working", state.auth.token);

  const laeuft = typeof timerEndsAt === "number" && timerEndsAt > Date.now();
  const pausiert =
    !laeuft &&
    typeof timerPausedRemainingSec === "number" &&
    timerPausedRemainingSec > 0;

  const restMmss =
    pausiert && typeof timerPausedRemainingSec === "number"
      ? `${Math.floor(timerPausedRemainingSec / 60)}:${String(
          timerPausedRemainingSec % 60,
        ).padStart(2, "0")}`
      : null;

  return (
    <div className="deck-root ar-slide ar-arbeit" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <CodeKulisse />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Ihre Zeit
        </div>
        <h1 className="deck-title rise rise-2">Beschreiben Sie, was Sie brauchen</h1>
      </header>

      <div className="ar-arbeit-mitte rise rise-3">
        {laeuft && typeof timerEndsAt === "number" ? (
          <Timer deadline={timerEndsAt} size="hero" />
        ) : pausiert ? (
          <div className="ar-arbeit-pause" data-testid="arbeit-pausiert">
            <span className="ar-arbeit-hero-ersatz">{restMmss}</span>
            <span className="ar-arbeit-pause-wort">Pause</span>
          </div>
        ) : (
          <span className="ar-arbeit-hero-ersatz" data-testid="arbeit-keine-uhr">
            08:00
          </span>
        )}
      </div>

      <footer className="ar-arbeit-fuss rise rise-4">
        {zahlen && zahlen.angemeldet > 0 ? (
          <p className="ar-arbeit-zahlen" data-testid="arbeit-zahlen">
            <strong>{zahlen.eingereicht}</strong> von{" "}
            <strong>{zahlen.angemeldet}</strong> abgeschickt
          </p>
        ) : (
          <p className="ar-arbeit-zahlen">Drei Fragen, eigene Worte.</p>
        )}
        <p className="ar-arbeit-hinweis">
          Es gibt keine falsche Antwort. Wer fertig ist, schickt ab.
        </p>
      </footer>
    </div>
  );
}
